// src/pages/Competitions/CompetitionResults.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { FaMedal, FaTrashAlt } from "react-icons/fa";
import { FiPrinter, FiDownload } from "react-icons/fi";
import { BiTrophy } from "react-icons/bi";

import {
  getCompetitionCompetiteursResults,
  getCompetitionCompetiteurs,
} from "../../services/competiteursServices";
import { getCompetitionById } from "../../services/competitionsServices";
import {
  getCompetitionDistributions,
  getFinishedDistributions,
  toggleRewarded,
  deleteDistribution,
} from "../../services/distributionsServices";
import ClubRankingPanel from "./components/ClubRankingPanel";

import ScrollIndicator from "../../hooks/ScrollIndicator";
import useScrollToTop from "../../hooks/useScrollToTop";
import Breadcrumb from "../../components/Breadcrumb";

const useDebounced = (value, delay = 300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

const downloadCSV = (rows, filename = "resultats.csv") => {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (val) =>
    `"${String(val ?? "")
      .replaceAll(`"`, `""`)
      .replaceAll("\n", " ")}"`;
  const body = rows
    .map((r) => headers.map((h) => escape(r[h])).join(";"))
    .join("\n");
  const csv = headers.join(";") + "\n" + body;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const pill =
  "inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-semibold";

const parseIds = (idsRaw) => {
  if (Array.isArray(idsRaw))
    return idsRaw.map((x) => Number(x)).filter(Boolean);
  if (typeof idsRaw === "string") {
    try {
      const p = JSON.parse(idsRaw || "[]");
      return Array.isArray(p) ? p.map((x) => Number(x)).filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const isPool = (f) => /^poule_/i.test(String(f || ""));
const isNC = (f) => String(f || "") === "non_classes";

const CompetitionResults = () => {
  useScrollToTop();
  const { competition_id } = useParams();

  // Data
  const [competition, setCompetition] = useState(null);
  const [results, setResults] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [allCompetiteurs, setAllCompetiteurs] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [displayMode, setDisplayMode] = useState("results"); // "results" | "distributions" | "clubs"

  // Filtres Résultats
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounced(searchTerm, 250);
  const [selectedClub, setSelectedClub] = useState("");
  const [filterGender, setFilterGender] = useState("all");
  const [podiumOnly, setPodiumOnly] = useState(false);
  const [sortBy, setSortBy] = useState("rank"); // rank | points | wins | name
  const [selectedDistributionId, setSelectedDistributionId] = useState("all"); // 👈 filtre

  // Filtres Distributions
  const [hideRewarded, setHideRewarded] = useState(false);
  const [distSearch, setDistSearch] = useState("");
  const debouncedDistSearch = useDebounced(distSearch, 250);
  const [filterTapis, setFilterTapis] = useState("all");
  const [filterFormule, setFilterFormule] = useState("all");
  const [filterDistGenre, setFilterDistGenre] = useState("all");
  const [distSortBy, setDistSortBy] = useState("tapis");

  const [allDistributions, setAllDistributions] = useState([]);

  // Fetch
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [comp, res, all, finishedDists, allDists] = await Promise.all([
          getCompetitionById(competition_id),
          getCompetitionCompetiteursResults(competition_id),
          getCompetitionCompetiteurs(competition_id),
          getFinishedDistributions(competition_id),
          getCompetitionDistributions(competition_id),
        ]);

        setCompetition(comp);
        setResults(Array.isArray(res) ? res : []);
        setAllCompetiteurs(Array.isArray(all) ? all : []);
        setDistributions(Array.isArray(finishedDists) ? finishedDists : []);
        setAllDistributions(Array.isArray(allDists) ? allDists : []);
      } catch (e) {
        console.error(e);
        toast.error("Erreur lors du chargement des données.");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [competition_id]);

  // Index competiteur -> distribution (distId, tapis, formule)
  const compToDist = useMemo(() => {
    const map = new Map();
    for (const d of distributions) {
      const ids = parseIds(d.competiteurs);
      for (const cid of ids) {
        map.set(Number(cid), {
          distId: d.id,
          tapis: d.numero_tapis,
          formule: d.formule_repartition,
        });
      }
    }
    return map;
  }, [distributions]);

  const distributionLocalNumberMap = useMemo(() => {
    const sorted = [...(allDistributions || [])].sort((a, b) => {
      const ai = Number(a.id);
      const bi = Number(b.id);

      if (!Number.isNaN(ai) && !Number.isNaN(bi)) return ai - bi;

      return String(a.id).localeCompare(String(b.id), "fr", {
        numeric: true,
        sensitivity: "base",
      });
    });

    const map = new Map();

    sorted.forEach((d, index) => {
      const key = Number(d.id) || d.id;
      map.set(key, index + 1);
    });

    return map;
  }, [allDistributions]);

  const getLocalDistNumber = (distId) => {
    const key = Number(distId) || distId;
    return distributionLocalNumberMap.get(key) ?? "—";
  };

  // Map des résultats "classés" par id
  const resultsById = useMemo(() => {
    const m = new Map();
    (results || []).forEach((r) => m.set(Number(r.id), r));
    return m;
  }, [results]);

  // ✅ Résultats complets = classés + NC (présents dans une distribution finie)
  const resultsWithDist = useMemo(() => {
    const base = (allCompetiteurs || []).filter((c) => {
      const id = Number(c.id);
      // on affiche tous ceux qui sont dans une distribution finie OU ceux qui sont déjà dans results
      return compToDist.has(id) || resultsById.has(id);
    });

    return base.map((c) => {
      const id = Number(c.id);
      const r = resultsById.get(id);

      // merge : les champs "résultats" écrasent ceux du compétiteur si présents
      const merged = { ...c, ...(r || {}) };

      // dist info
      const info = compToDist.get(id);
      merged._distId = info?.distId ?? null;
      merged._distLocalNumber =
        info?.distId != null ? getLocalDistNumber(info.distId) : null;
      merged._tapis = info?.tapis ?? null;
      merged._distFormule = info?.formule ?? null;

      // ✅ si pas classé => NC
      const rankNum = Number(merged.classement_position);
      const hasRank = Number.isFinite(rankNum) && rankNum > 0;
      if (!hasRank) merged.classement_position = "NC";

      // defaults
      if (merged.total_points == null) merged.total_points = 0;
      if (merged.total_victoires == null) merged.total_victoires = 0;

      return merged;
    });
  }, [allCompetiteurs, compToDist, resultsById]);

  const clubs = useMemo(() => {
    const c = new Set(
      (resultsWithDist || []).map((r) => r.club_competiteur).filter(Boolean),
    );
    return ["", ...Array.from(c).sort((a, b) => a.localeCompare(b))];
  }, [resultsWithDist]);

  const distributionOptions = useMemo(() => {
    const arr = [...new Set(distributions.map((d) => d.id))].map((id) => {
      const d = distributions.find((x) => x.id === id);
      const nc = isNC(d?.formule_repartition);
      const localNumber = getLocalDistNumber(id);

      return {
        id,
        label: nc
          ? `#${localNumber} — NC`
          : `#${localNumber} — Tapis ${d?.numero_tapis ?? "?"}`,
        tapis: d?.numero_tapis ?? 0,
      };
    });

    arr.sort(
      (a, b) => (a.tapis || 0) - (b.tapis || 0) || Number(a.id) - Number(b.id),
    );

    return [{ id: "all", label: "Toutes distributions" }, ...arr];
  }, [distributions, distributionLocalNumberMap]);

  const stats = useMemo(() => {
    const total = resultsWithDist.length;
    const uniqueClubs = new Set(resultsWithDist.map((r) => r.club_competiteur))
      .size;
    const podiums = resultsWithDist.filter((r) =>
      [1, 2, 3].includes(Number(r.classement_position)),
    ).length;

    // ✅ plus cohérent : uniquement les poules récompensées
    const rewarded = distributions.filter(
      (d) => !isNC(d.formule_repartition) && d.is_rewarded,
    ).length;

    return { total, uniqueClubs, podiums, rewarded };
  }, [resultsWithDist, distributions]);

  // Filtrage Résultats
  const filteredResults = useMemo(() => {
    let arr = resultsWithDist;

    const q = debouncedSearch.trim().toLowerCase();
    if (q) {
      arr = arr.filter((c) => {
        const hay = [
          c.nom_competiteur,
          c.prenom_competiteur,
          c.club_competiteur,
          c._distLocalNumber ? `#${c._distLocalNumber}` : "",
          c._distId ? `id ${c._distId}` : "",
          c._tapis != null ? `tapis ${c._tapis}` : "",
          isNC(c._distFormule) ? "nc non classé non classés" : "",
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }

    if (selectedClub)
      arr = arr.filter((c) => c.club_competiteur === selectedClub);

    if (filterGender !== "all") {
      arr = arr.filter(
        (c) => (c.genre_competiteur || "").toUpperCase() === filterGender,
      );
    }

    if (podiumOnly) {
      arr = arr.filter((c) =>
        [1, 2, 3].includes(Number(c.classement_position)),
      );
    }

    if (selectedDistributionId !== "all") {
      arr = arr.filter(
        (c) => String(c._distId) === String(selectedDistributionId),
      );
    }

    // Tri
    const safeNum = (v, fallback = 9999) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback; // ✅ "NC" => 9999
    };
    const clone = [...arr];

    if (sortBy === "points") {
      clone.sort((a, b) => safeNum(b.total_points) - safeNum(a.total_points));
    } else if (sortBy === "wins") {
      clone.sort(
        (a, b) => safeNum(b.total_victoires) - safeNum(a.total_victoires),
      );
    } else if (sortBy === "name") {
      clone.sort((a, b) =>
        `${a.nom_competiteur} ${a.prenom_competiteur}`.localeCompare(
          `${b.nom_competiteur} ${b.prenom_competiteur}`,
        ),
      );
    } else {
      // ✅ rank : NC en bas
      const rankVal = (x) =>
        isNC(x._distFormule) ? 9998 : safeNum(x.classement_position || 9999);
      clone.sort((a, b) => rankVal(a) - rankVal(b));
    }

    return clone;
  }, [
    resultsWithDist,
    debouncedSearch,
    selectedClub,
    filterGender,
    podiumOnly,
    sortBy,
    selectedDistributionId,
  ]);

  // Filtres Distributions (options)
  const distFiltersOptions = useMemo(() => {
    const tapis = Array.from(
      new Set(distributions.map((d) => d.numero_tapis).filter(Boolean)),
    ).sort((a, b) => (a ?? 0) - (b ?? 0));

    const formules = Array.from(
      new Set(
        distributions
          .map((d) => d.formule_repartition)
          .filter(Boolean)
          .map((s) => String(s).replaceAll("_", " ")),
      ),
    ).sort((a, b) => a.localeCompare(b));

    return { tapis: ["all", ...tapis], formules: ["all", ...formules] };
  }, [distributions]);

  const filteredDistributions = useMemo(() => {
    let arr = distributions;

    // ✅ masque les distributions récompensées (on garde NC visibles si besoin)
    if (hideRewarded) {
      arr = arr.filter((d) => isNC(d.formule_repartition) || !d.is_rewarded);
    }

    const q = debouncedDistSearch.trim().toLowerCase();
    if (q) {
      arr = arr.filter((d) => {
        const txt = [
          d.categorie_age,
          d.categorie_poids,
          d.formule_repartition,
          isNC(d.formule_repartition) ? "NC" : `Tapis ${d.numero_tapis}`,
          `#${getLocalDistNumber(d.id)}`,
          `id ${d.id}`,
        ]
          .join(" ")
          .toLowerCase()
          .replaceAll("_", " ");
        return txt.includes(q);
      });
    }

    if (filterTapis !== "all") {
      arr = arr.filter((d) => String(d.numero_tapis) === String(filterTapis));
    }

    if (filterFormule !== "all") {
      arr = arr.filter(
        (d) =>
          String(d.formule_repartition).replaceAll("_", " ") === filterFormule,
      );
    }

    if (filterDistGenre !== "all") {
      arr = arr.filter(
        (d) => (d.genre || "").toUpperCase() === filterDistGenre,
      );
    }

    const clone = [...arr];

    if (distSortBy === "age") {
      clone.sort((a, b) =>
        String(a.categorie_age).localeCompare(String(b.categorie_age)),
      );
    } else if (distSortBy === "poids") {
      clone.sort((a, b) =>
        String(a.categorie_poids).localeCompare(String(b.categorie_poids)),
      );
    } else if (distSortBy === "rewarded") {
      // ✅ poules d'abord non récompensées
      clone.sort((a, b) => {
        const aPool = isPool(a.formule_repartition);
        const bPool = isPool(b.formule_repartition);
        if (aPool !== bPool) return aPool ? -1 : 1;
        return Number(a.is_rewarded) - Number(b.is_rewarded);
      });
    } else {
      // tapis : NC en bas (tapis null/0)
      clone.sort((a, b) => {
        const aNc = isNC(a.formule_repartition);
        const bNc = isNC(b.formule_repartition);
        if (aNc !== bNc) return aNc ? 1 : -1;
        return (a.numero_tapis || 0) - (b.numero_tapis || 0);
      });
    }

    return clone;
  }, [
    distributions,
    hideRewarded,
    debouncedDistSearch,
    filterTapis,
    filterFormule,
    filterDistGenre,
    distSortBy,
  ]);

  const getCompetiteursForDistribution = (distribution, allCompetiteurs) => {
    const ids = parseIds(distribution?.competiteurs);
    const idSet = new Set((ids || []).map((x) => Number(x)));

    const base = (allCompetiteurs || []).filter((comp) =>
      idSet.has(Number(comp.id)),
    );

    // ✅ NC : tri alphabétique
    if (isNC(distribution?.formule_repartition)) {
      return [...base].sort((a, b) =>
        `${a.nom_competiteur} ${a.prenom_competiteur}`.localeCompare(
          `${b.nom_competiteur} ${b.prenom_competiteur}`,
        ),
      );
    }

    // sinon : tri par classement
    return [...base].sort(
      (a, b) =>
        (Number(a.classement_position) || 9999) -
        (Number(b.classement_position) || 9999),
    );
  };

  const handleDeleteEmptyDistribution = async (dist) => {
    if (!dist?.id) return;
    if (
      !window.confirm(
        `Supprimer la distribution #${getLocalDistNumber(dist.id)} ? (elle est vide)`,
      )
    )
      return;

    try {
      await deleteDistribution(dist.id);

      setDistributions((prev) => prev.filter((d) => d.id !== dist.id));

      // si on filtrait dessus : reset
      setSelectedDistributionId((prev) =>
        String(prev) === String(dist.id) ? "all" : prev,
      );

      toast.success("Distribution supprimée.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression de la distribution.");
    }
  };

  if (loading) {
    return (
      <div className="mt-40 text-center text-gray-500 dark:text-gray-300">
        Chargement des résultats…
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full mx-auto p-3 mt-[130px] dark:text-white">
      <ScrollIndicator />
      <div className="text-center mb-6">
        <h2 className="text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
          Organisateur
        </h2>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">
          Résultats — {competition?.nom_competition}
        </h1>
      </div>

      <div className="mb-4 max-w-[1280px] w-full mx-auto">
        <Breadcrumb competitionName={competition?.nom_competition} />
      </div>

      {/* Stat Cards */}
      <div className="mx-auto max-w-[1280px] w-full grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="rounded-2xl bg-white/70 dark:bg-slate-900/60 border border-gray-200/70 dark:border-slate-700/70 p-3 shadow-sm">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Judokas (résultats)
          </div>
          <div className="text-2xl font-bold">{stats.total}</div>
        </div>
        <div className="rounded-2xl bg-white/70 dark:bg-slate-900/60 border border-gray-200/70 dark:border-slate-700/70 p-3 shadow-sm">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Clubs représentés
          </div>
          <div className="text-2xl font-bold">{stats.uniqueClubs}</div>
        </div>
        <div className="rounded-2xl bg-white/70 dark:bg-slate-900/60 border border-gray-200/70 dark:border-slate-700/70 p-3 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Podiums
            </div>
            <BiTrophy className="opacity-70" />
          </div>
          <div className="text-2xl font-bold">{stats.podiums}</div>
        </div>
        <div className="rounded-2xl bg-white/70 dark:bg-slate-900/60 border border-gray-200/70 dark:border-slate-700/70 p-3 shadow-sm">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Poules récompensées
          </div>
          <div className="text-2xl font-bold">{stats.rewarded}</div>
        </div>
      </div>

      {/* Switch */}
      <div className="flex justify-center gap-2 mb-4">
        <button
          onClick={() => setDisplayMode("results")}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition
          ${
            displayMode === "results"
              ? "bg-blue-600 text-white"
              : "bg-white/70 dark:bg-slate-900/60 border border-gray-200/70 dark:border-slate-700/70 text-gray-800 dark:text-white"
          }`}
        >
          Résultats par Judoka
        </button>

        <button
          onClick={() => setDisplayMode("distributions")}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition
          ${
            displayMode === "distributions"
              ? "bg-blue-600 text-white"
              : "bg-white/70 dark:bg-slate-900/60 border border-gray-200/70 dark:border-slate-700/70 text-gray-800 dark:text-white"
          }`}
        >
          Résultats par Poules
        </button>

        <button
          onClick={() => setDisplayMode("clubs")}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition
          ${
            displayMode === "clubs"
              ? "bg-blue-600 text-white"
              : "bg-white/70 dark:bg-slate-900/60 border border-gray-200/70 dark:border-slate-700/70 text-gray-800 dark:text-white"
          }`}
        >
          Classement Clubs
        </button>
      </div>

      {/* TOOLBAR */}
      {displayMode === "results" ? (
        <div className="mx-auto max-w-[1280px] w-full mb-4">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur p-3 border border-gray-200/70 dark:border-slate-700/70 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 flex-1">
              <input
                type="text"
                placeholder="Rechercher (nom, prénom, club, #dist, tapis, NC)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white p-2 rounded-md"
              />
              <select
                value={selectedClub}
                onChange={(e) => setSelectedClub(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white p-2 rounded-md"
              >
                {clubs.map((c, i) => (
                  <option key={i} value={c}>
                    {c ? c : "Tous les clubs"}
                  </option>
                ))}
              </select>
              <select
                value={filterGender}
                onChange={(e) => setFilterGender(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white p-2 rounded-md"
              >
                <option value="all">Tous genres</option>
                <option value="M">Masculin</option>
                <option value="F">Féminin</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white p-2 rounded-md"
              >
                <option value="rank">Tri: Classement</option>
                <option value="points">Tri: Points</option>
                <option value="wins">Tri: Victoires</option>
                <option value="name">Tri: Nom</option>
              </select>
              <select
                value={selectedDistributionId}
                onChange={(e) => setSelectedDistributionId(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white p-2 rounded-md"
              >
                {distributionOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={podiumOnly}
                  onChange={(e) => setPodiumOnly(e.target.checked)}
                />
                <span className="text-sm">Podium uniquement</span>
              </label>

              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200/70 dark:border-slate-700/70 px-3 py-2 text-sm hover:bg-white dark:hover:bg-slate-800"
                title="Imprimer"
              >
                <FiPrinter /> Imprimer
              </button>

              <button
                onClick={() =>
                  downloadCSV(filteredResults, "resultats_judokas.csv")
                }
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 text-white px-3 py-2 text-sm hover:bg-blue-700"
                title="Exporter CSV"
              >
                <FiDownload /> Export CSV
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mx-auto max-w-[1280px] w-full mb-4">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur p-3 border border-gray-200/70 dark:border-slate-700/70 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 flex-1">
              <input
                type="text"
                placeholder="Filtrer (tapis, âge, poids, formule, #id, NC)"
                value={distSearch}
                onChange={(e) => setDistSearch(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white p-2 rounded-md"
              />
              <select
                value={filterTapis}
                onChange={(e) => setFilterTapis(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white p-2 rounded-md"
              >
                {distFiltersOptions.tapis.map((t) => (
                  <option key={t} value={t}>
                    {t === "all" ? "Tous tapis" : `Tapis ${t}`}
                  </option>
                ))}
              </select>
              <select
                value={filterFormule}
                onChange={(e) => setFilterFormule(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white p-2 rounded-md"
              >
                {distFiltersOptions.formules.map((f) => (
                  <option key={f} value={f}>
                    {f === "all" ? "Toutes formules" : f}
                  </option>
                ))}
              </select>
              <select
                value={filterDistGenre}
                onChange={(e) => setFilterDistGenre(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white p-2 rounded-md"
              >
                <option value="all">Tous genres</option>
                <option value="M">Masculin</option>
                <option value="F">Féminin</option>
              </select>
              <select
                value={distSortBy}
                onChange={(e) => setDistSortBy(e.target.value)}
                className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white p-2 rounded-md"
              >
                <option value="tapis">Tri: Tapis</option>
                <option value="age">Tri: Cat. d’âge</option>
                <option value="poids">Tri: Cat. de poids</option>
                <option value="rewarded">Tri: À récompenser d’abord</option>
              </select>
            </div>

            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={hideRewarded}
                  onChange={(e) => setHideRewarded(e.target.checked)}
                />
                <span className="text-sm">Masquer les poules récompensées</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* CONTENU */}
      {displayMode === "results" ? (
        filteredResults.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-400">
            Aucun compétiteur ne correspond à vos filtres.
          </p>
        ) : (
          <div className="overflow-x-auto w-full mx-auto max-w-[1280px] bg-white dark:bg-slate-900 rounded-2xl shadow-lg border border-gray-200/70 dark:border-slate-700/70">
            <table className="table-auto border-collapse w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-800/70 text-gray-700 dark:text-gray-200 text-xs uppercase tracking-wide">
                  <th className="border-b px-3 py-2 text-left">Nom</th>
                  <th className="border-b px-3 py-2 text-left">Club</th>
                  <th className="border-b px-3 py-2">Genre</th>
                  <th className="border-b px-3 py-2">Poids</th>
                  <th className="border-b px-3 py-2">Victoires</th>
                  <th className="border-b px-3 py-2">Points</th>
                  <th className="border-b px-3 py-2">Distribution</th>
                  <th className="border-b px-3 py-2">Classement</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((c) => {
                  const rowIsNC = isNC(c._distFormule);
                  const rankNum = Number(c.classement_position || 0);

                  return (
                    <tr
                      key={c.id}
                      className="odd:bg-white even:bg-gray-50/70 dark:odd:bg-slate-900 dark:even:bg-slate-900/70 border-b border-gray-100 dark:border-slate-800 text-sm text-center"
                    >
                      <td className="px-3 py-2 text-left">
                        <div className="font-semibold">
                          {c.nom_competiteur} {c.prenom_competiteur}
                        </div>
                        <div className="text-xs text-gray-500">
                          {c.licence_competiteur || ""}
                        </div>
                      </td>

                      <td className="px-3 py-2 text-left">
                        {c.club_competiteur}
                      </td>

                      <td className="px-3 py-2">
                        <span
                          className={`${pill} ${
                            (c.genre_competiteur || "").toUpperCase() === "M"
                              ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200"
                              : "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-200"
                          }`}
                        >
                          {(c.genre_competiteur || "—").toUpperCase()}
                        </span>
                      </td>

                      <td className="px-3 py-2">
                        {c.poids_competiteur != null
                          ? `${c.poids_competiteur} kg`
                          : "—"}
                      </td>

                      <td className="px-3 py-2 font-semibold">
                        {c.total_victoires ?? 0}
                      </td>
                      <td className="px-3 py-2 font-semibold">
                        {c.total_points ?? 0}
                      </td>

                      {/* Distribution */}
                      <td className="px-3 py-2">
                        {c._distId ? (
                          <button
                            onClick={() =>
                              setSelectedDistributionId(String(c._distId))
                            }
                            className={`${pill} bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 hover:opacity-90`}
                            title={`Filtrer sur la distribution #${c._distLocalNumber} (ID technique : ${c._distId})`}
                          >
                            #{c._distLocalNumber}
                            {rowIsNC ? (
                              <span className="ml-2 opacity-70">— NC</span>
                            ) : c._tapis != null ? (
                              <span className="ml-2 opacity-70">
                                — Tapis {c._tapis}
                              </span>
                            ) : null}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-500">—</span>
                        )}
                      </td>

                      {/* Classement */}
                      <td className="px-3 py-2 font-extrabold">
                        <div className="inline-flex items-center gap-2">
                          <span>
                            {rowIsNC ? "NC" : rankNum > 0 ? rankNum : "—"}
                          </span>

                          {!rowIsNC && [1, 2, 3].includes(rankNum) && (
                            <span
                              className={`${pill} bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200`}
                            >
                              <FaMedal className="mr-1" />
                              {rankNum === 1
                                ? "Or"
                                : rankNum === 2
                                  ? "Argent"
                                  : "Bronze"}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : displayMode === "distributions" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-[1280px] mx-auto">
          {filteredDistributions.map((dist) => {
            const comps = getCompetiteursForDistribution(dist, allCompetiteurs);
            const distIsNC = isNC(dist.formule_repartition);
            const empty = comps.length === 0;

            const headerLeft = `#${getLocalDistNumber(dist.id)} — ${
              distIsNC ? "NC" : `Tapis ${dist.numero_tapis}`
            } — ${dist.categorie_age} — ${
              (dist.genre || "M").toUpperCase() === "M" ? "Masculin" : "Féminin"
            }`;

            return (
              <div
                key={dist.id}
                className="bg-white dark:bg-slate-900 shadow-lg rounded-2xl overflow-hidden border border-gray-200/70 dark:border-slate-700/70 flex flex-col"
              >
                <div
                  className="bg-blue-700 text-white px-4 py-2 text-sm font-bold tracking-wider uppercase"
                  title={`ID technique : ${dist.id}`}
                >
                  {headerLeft}
                </div>

                <div className="px-4 py-3 text-sm text-gray-800 dark:text-gray-300 bg-gray-50 dark:bg-slate-800/50 flex items-center justify-between gap-2">
                  <div className="space-y-1">
                    <div>
                      <span className="font-semibold">Poids :</span>{" "}
                      {dist.categorie_poids}
                    </div>
                    <div className="text-xs">
                      <span className="font-semibold">Formule :</span>{" "}
                      {distIsNC
                        ? "Non classés (NC)"
                        : String(dist.formule_repartition || "")
                            .replaceAll("_", " ")
                            .trim() || "—"}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* ✅ bouton récompense pour toutes les distributions (hors NC) */}
                    {!distIsNC && !empty && (
                      <button
                        onClick={async () => {
                          try {
                            const updated = await toggleRewarded(dist.id);
                            setDistributions((prev) =>
                              prev.map((d) =>
                                d.id === dist.id
                                  ? { ...d, is_rewarded: updated.is_rewarded }
                                  : d,
                              ),
                            );
                            toast.success(
                              updated.message || "Statut mis à jour.",
                            );
                          } catch (error) {
                            toast.error(
                              "Erreur lors du changement de statut récompense.",
                            );
                          }
                        }}
                        className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition ${
                          dist.is_rewarded
                            ? "bg-yellow-500 text-white hover:bg-yellow-600"
                            : "bg-gray-300 text-gray-800 hover:bg-gray-400 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600"
                        }`}
                        title={
                          dist.is_rewarded
                            ? "Médailles remises"
                            : "À récompenser"
                        }
                      >
                        <FaMedal className="text-lg" />
                        {dist.is_rewarded
                          ? "Médailles remises"
                          : "À récompenser"}
                      </button>
                    )}

                    {/* ✅ suppression si distribution vide */}
                    {empty && (
                      <button
                        onClick={() => handleDeleteEmptyDistribution(dist)}
                        className="flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-rose-600 text-white hover:bg-rose-700"
                        title="Supprimer cette distribution vide"
                      >
                        <FaTrashAlt className="text-base" />
                        Supprimer
                      </button>
                    )}
                  </div>
                </div>

                <ul className="divide-y divide-gray-200 dark:divide-slate-700 px-4 py-3">
                  {comps.map((c) => {
                    const rankNum = Number(c.classement_position || 0);
                    return (
                      <li
                        key={c.id}
                        className="py-2 flex items-center justify-between"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                            {c.nom_competiteur} {c.prenom_competiteur}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {c.club_competiteur}
                          </p>
                        </div>

                        <div className="text-right">
                          <div className="text-lg font-extrabold">
                            {distIsNC ? "NC" : rankNum > 0 ? rankNum : "—"}
                          </div>

                          {!distIsNC && [1, 2, 3].includes(rankNum) && (
                            <div className="text-xs text-yellow-700 dark:text-yellow-300 flex items-center justify-end gap-1">
                              <FaMedal /> Podium
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}

                  {comps.length === 0 && (
                    <li className="py-3 text-sm text-gray-500 dark:text-gray-400">
                      Aucun compétiteur dans cette distribution.
                    </li>
                  )}
                </ul>
              </div>
            );
          })}

          {filteredDistributions.length === 0 && (
            <div className="col-span-full text-center text-gray-500 dark:text-gray-400">
              Aucune distribution ne correspond à vos filtres.
            </div>
          )}
        </div>
      ) : (
        <div className="mt-4">
          <ClubRankingPanel results={resultsWithDist} />
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default CompetitionResults;
